<script setup lang="ts">
/* UTILS */
import { computed } from 'vue';
import { RouterView } from 'vue-router';
import { useCssBreakpoints, ALAppLoader, ALToaster, ALLocaleProvider } from '@af-lib/vue-ui';

import MobileDisclaimerBanner from '@/components/banner/MobileDisclaimerBanner.vue';
import ExternalRedirectionModal from '@/components/modal/ExternalRedirectionModal.vue';
import UserAlreadyInviteModal from '@/components/modal/UserAlreadyInviteModal.vue';
/* COMPONENTS */
import NavBar from '@/features/navbar/NavBar.vue';
import CongratsModal from '@/FNX-Lib/modal/CongratsModal.vue';
import { useAuthStore } from '@/stores/auth';
/* STORES */
import { useEnumsStore } from '@/stores/enums.store';
import { useGlobalStore } from '@/stores/global';
import { useLocaleStore } from '@/stores/locales';

import HubspotSupport from './features/hubspot-support/HubspotSupport.vue';

const { isMobile } = useCssBreakpoints();
const globalStore = useGlobalStore();
const authStore = useAuthStore();
const localeStore = useLocaleStore();
const enumsStore = useEnumsStore();

const isAppLoading = computed(() => globalStore.isLoading);
const locale = computed(() => localeStore.getCurrentISO);
const isRtl = computed(() => localeStore.getIsLocaleRtl);

/**
 * Load acronyms from static data when login is done
 * Launch the loading as soon as possible, as it can take several seconds to get the data
 */
const getAcronymsEnabled = computed(() => {
  return authStore.getIsLoginDone;
});

/* PRELOAD STATIC DATA */
if (getAcronymsEnabled.value) {
  enumsStore.loadJobsAcronyms();
}

/* SETUP */
// Check if App is in an iframe to disable the navbar
const isInIframe = window.self !== window.top;

// set global indicator of focus type - @TODO migrate to af lib 2 if it seems to be an adequate solution
const setModality = (m: 'pointer' | 'keyboard') => {
  document.documentElement.dataset.modality = m;
};
window.addEventListener('pointerdown', () => setModality('pointer'), true);
window.addEventListener(
  'keydown',
  e => {
    if (['Tab', 'Escape', 'Enter', ' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key))
      setModality('keyboard');
  },
  true
);
</script>

<template>
  <v-locale-provider :class="isRtl ? 'app-rtl' : undefined" :rtl="isRtl">
    <v-app>
      <ALLocaleProvider>
        <v-fade-transition>
          <ALAppLoader v-if="isAppLoading" />
        </v-fade-transition>

        <!-- Teleported component -->
        <div id="af-app" />
        <!-- App components in loading condition -->
        <div v-if="!isAppLoading" :key="locale" class="h-100">
          <!-- Main NavBar hidden when iframe mode -->
          <NavBar v-if="!isInIframe" />

          <v-main class="h-100" role="main">
            <RouterView />
          </v-main>
          <MobileDisclaimerBanner v-if="isMobile" />
          <UserAlreadyInviteModal />
        </div>

        <!-- External Redirection modal -->
        <ExternalRedirectionModal />
        <!-- Congrats modal -->
        <CongratsModal />
        <!-- Hubspot Support -->
        <HubspotSupport />

        <!-- TOASTER -->
        <ALToaster />
      </ALLocaleProvider>
    </v-app>
  </v-locale-provider>
</template>

<style lang="scss" scoped>
// Flip globally needed fontawesome icons
.app-rtl :deep([class*='fa-arrow']),
.app-rtl :deep([class*='fa-chevron']) {
  transform: scaleX(-1);
}
</style>
